import type { GameStateManager } from '../game/GameState';
import type { GameData, RealityBreachResult, RealityModifierEffect, GameSpeed } from '../game/types';
import type { RealityBreachSystem } from '../systems/RealityBreachSystem';
import { AudioManager } from '../audio/AudioManager';

export class RealityBreachModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private realityBreachSystem: RealityBreachSystem,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: RealityBreachResult): void {
    this.render(result);
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    AudioManager.getInstance().playEffect();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    const result = this.gameState.getPendingRealityBreach();
    
    if (result) {
      // Apply the reality modifier effects
      this.realityBreachSystem.applyRealityModifier(result);
    }
    
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    this.gameState.dequeueRealityBreach();
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private formatEffectDescription(effect: RealityModifierEffect): string[] {
    const descriptions: string[] = [];

    if (effect.allLoseMaxSanity !== undefined) {
      descriptions.push(`All researchers lose ${effect.allLoseMaxSanity} maximum sanity`);
    }
    
    if (effect.recruitmentForbidden !== undefined && effect.recruitmentForbidden) {
      descriptions.push(`Recruitment is now forbidden`);
    }
    
    if (effect.loseChemCapacity !== undefined) {
      descriptions.push(`Chemical capacity reduced to ${effect.loseChemCapacity}`);
    }
    
    if (effect.movementTimeMultiplier !== undefined) {
      descriptions.push(`Movement time increased by ${effect.movementTimeMultiplier}x`);
    }
    
    if (effect.extraBudjetLossPerTick !== undefined) {
      descriptions.push(`Budget drain increased by ${effect.extraBudjetLossPerTick}/tick (Phantom Researcher)`);
    }
    
    if (effect.cellDisappears !== undefined) {
      descriptions.push(`Cell ${effect.cellDisappears} has been lost in time and space`);
    }
    
    if (effect.budgetLoss !== undefined) {
      descriptions.push(`Budget reduced by ${effect.budgetLoss}`);
    }
    
    if (effect.killRandomResearcher !== undefined && effect.killRandomResearcher) {
      descriptions.push(`A researcher has been claimed by the breach`);
    }

    return descriptions;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private render(result: RealityBreachResult): void {
    const effectDescriptions = result.effects.flatMap(e => this.formatEffectDescription(e));
    
    // Handle null/empty values gracefully
    const modifierName = result.modifierName?.trim() || 'Unknown Breach';
    const modifierDescription = result.modifierDescription?.trim() || 'The nature of this reality breach is unknown.';

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content reality-breach-modal" tabindex="0">
        <div class="modal-header">
          <h2>⚠️ Reality Breach</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="breach-intro">
            <p class="breach-warning-text">
              Despite efforts to anchor the facility in reality, Hyperreal entities have managed to warp our existence.
            </p>
            <p class="breach-healing">
              Reality attempts to heal its wounds: <strong>+4 Anchoring</strong>
            </p>
          </div>
          
          <div class="modifier-info">
            <h3 class="modifier-name">${this.escapeHtml(modifierName)}</h3>
            <p class="modifier-description">${this.escapeHtml(modifierDescription)}</p>
          </div>

          <div class="effects-section">
            <h4>Effects:</h4>
            <ul class="effects-list">
              ${effectDescriptions.map(desc => `<li class="effect-item">${desc}</li>`).join('')}
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Acknowledge</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }
  }
}
